import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Camera, Video, Image, X, Loader2, Trash2, 
  Play, Pause, AlertCircle, CheckCircle, Film, Images, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Media categories
const CATEGORIES = [
  { id: 'before', label: 'Before', color: 'bg-blue-600' },
  { id: 'after', label: 'After', color: 'bg-green-600' },
  { id: 'damage', label: 'Damage Documentation', color: 'bg-red-600' }
];

// ============ BULK UPLOAD COMPONENT ============
export function BulkMediaUpload({ bookingId, onUploadComplete }) {
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('before');
  const [files, setFiles] = useState([]); // Array of {file, preview, status, progress}
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  const handleFilesSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    const errors = [];

    selectedFiles.forEach(file => {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      
      if (!isVideo && !isImage) {
        errors.push(`${file.name}: Not an image or video`);
        return;
      }

      const maxSize = isVideo ? 50 * 1024 * 1024 : 20 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: Too large (max ${isVideo ? '50MB' : '20MB'})`);
        return;
      }

      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending', // pending, uploading, success, error
        progress: 0,
        isVideo
      });
    });

    if (errors.length > 0) {
      toast.error(`${errors.length} file(s) skipped`, {
        description: errors.slice(0, 3).join('\n')
      });
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadAll = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status === 'success') {
        successCount++;
        continue;
      }

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));

      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        formData.append('category', selectedCategory);
        formData.append('notes', '');

        const res = await fetch(`${API}/api/workshop/job/${bookingId}/media`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (res.ok) {
          setFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'success' } : f
          ));
          successCount++;
        } else {
          setFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'error' } : f
          ));
          errorCount++;
        }
      } catch (e) {
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error' } : f
        ));
        errorCount++;
      }

      setUploadProgress({ current: i + 1, total: files.length });
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully!`);
      onUploadComplete?.();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} file(s) failed to upload`);
    }

    // Clear successful uploads after a delay
    setTimeout(() => {
      setFiles(prev => prev.filter(f => f.status !== 'success'));
    }, 2000);
  };

  const pendingCount = files.filter(f => f.status === 'pending' || f.status === 'error').length;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        data-testid="bulk-upload-btn"
      >
        <Images className="w-4 h-4" />
        <span className="hidden sm:inline">Bulk Upload</span>
      </button>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !uploading && setShowModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
              data-testid="bulk-upload-modal"
            >
              {/* Header */}
              <div className="border-b border-zinc-800 p-4 flex items-center justify-between shrink-0">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Images className="w-5 h-5 text-blue-500" />
                  Bulk Upload Photos
                </h2>
                <button 
                  onClick={() => !uploading && setShowModal(false)} 
                  disabled={uploading}
                  className="text-zinc-500 hover:text-white disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Category for all uploads *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => !uploading && setSelectedCategory(cat.id)}
                        disabled={uploading}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedCategory === cat.id
                            ? `${cat.color} text-white`
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        } disabled:opacity-50`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Drop Zone */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFilesSelect}
                    disabled={uploading}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-2 border-dashed border-zinc-700 rounded-xl p-6 flex flex-col items-center gap-3 hover:border-blue-500 hover:bg-zinc-800/50 transition-colors disabled:opacity-50"
                  >
                    <div className="flex gap-4">
                      <Camera className="w-8 h-8 text-zinc-500" />
                      <Video className="w-8 h-8 text-zinc-500" />
                    </div>
                    <div className="text-center">
                      <p className="text-white font-medium">Click to select multiple files</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Images up to 20MB • Videos up to 50MB each
                      </p>
                    </div>
                  </button>
                </div>

                {/* Files Preview Grid */}
                {files.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-white">
                        Selected Files ({files.length})
                      </h3>
                      {!uploading && files.length > 0 && (
                        <button
                          onClick={() => setFiles([])}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                      {files.map((item, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 group">
                          {item.isVideo ? (
                            <video src={item.preview} className="w-full h-full object-cover" />
                          ) : (
                            <img src={item.preview} alt="" className="w-full h-full object-cover" />
                          )}
                          
                          {/* Status Overlay */}
                          <div className={`absolute inset-0 flex items-center justify-center ${
                            item.status === 'uploading' ? 'bg-black/60' :
                            item.status === 'success' ? 'bg-green-600/60' :
                            item.status === 'error' ? 'bg-red-600/60' :
                            'bg-black/0 group-hover:bg-black/40'
                          } transition-colors`}>
                            {item.status === 'uploading' && (
                              <Loader2 className="w-6 h-6 text-white animate-spin" />
                            )}
                            {item.status === 'success' && (
                              <CheckCircle className="w-6 h-6 text-white" />
                            )}
                            {item.status === 'error' && (
                              <AlertCircle className="w-6 h-6 text-white" />
                            )}
                            {item.status === 'pending' && !uploading && (
                              <button
                                onClick={() => removeFile(idx)}
                                className="p-1.5 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4 text-white" />
                              </button>
                            )}
                          </div>

                          {/* Video badge */}
                          {item.isVideo && (
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-[10px] text-white flex items-center gap-1">
                              <Video className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Add More Button */}
                      {!uploading && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center hover:border-zinc-500 transition-colors"
                        >
                          <Plus className="w-8 h-8 text-zinc-600" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div className="bg-zinc-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white">Uploading...</span>
                      <span className="text-sm text-zinc-400">
                        {uploadProgress.current} / {uploadProgress.total}
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-zinc-800 p-4 flex gap-3 shrink-0">
                <button
                  onClick={() => !uploading && setShowModal(false)}
                  disabled={uploading}
                  className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold disabled:opacity-50"
                >
                  {uploading ? 'Please wait...' : 'Cancel'}
                </button>
                <button
                  onClick={uploadAll}
                  disabled={uploading || pendingCount === 0}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="start-bulk-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload {pendingCount} File{pendingCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ SINGLE FILE UPLOAD COMPONENT ============
export function MediaUploadButton({ bookingId, onUploadComplete, category = null }) {
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(category || 'before');
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    if (!isVideo && !isImage) {
      toast.error('Please select an image or video file');
      return;
    }

    // Check size limits
    const maxSize = isVideo ? 50 * 1024 * 1024 : 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '50MB' : '20MB'}`);
      return;
    }

    // For videos, check duration (1:30 max)
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (video.duration > 90) {
          toast.error('Video too long. Maximum 1:30');
          return;
        }
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
      };
      video.src = URL.createObjectURL(file);
    } else {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      formData.append('notes', notes);

      const res = await fetch(`${API}/api/workshop/job/${bookingId}/media`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Media uploaded successfully!');
        setShowModal(false);
        setSelectedFile(null);
        setPreview(null);
        setNotes('');
        onUploadComplete?.(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const isVideo = selectedFile?.type.startsWith('video/');

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
        data-testid="upload-media-btn"
      >
        <Upload className="w-4 h-4" />
        <span className="hidden sm:inline">Upload Media</span>
      </button>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="media-upload-modal"
            >
              {/* Header */}
              <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Film className="w-5 h-5 text-[#C41E3A]" />
                  Upload Photo/Video
                </h2>
                <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Category *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedCategory === cat.id
                            ? `${cat.color} text-white`
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* File Selection */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Select File *</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {!selectedFile ? (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-zinc-700 rounded-xl p-8 flex flex-col items-center gap-3 hover:border-zinc-500 transition-colors"
                    >
                      <div className="flex gap-4">
                        <Camera className="w-8 h-8 text-zinc-500" />
                        <Video className="w-8 h-8 text-zinc-500" />
                      </div>
                      <div className="text-center">
                        <p className="text-white font-medium">Click to select</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Images up to 20MB • Videos up to 50MB (max 1:30)
                        </p>
                      </div>
                    </button>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden bg-zinc-800">
                      {isVideo ? (
                        <video
                          src={preview}
                          controls
                          className="w-full max-h-64 object-contain"
                        />
                      ) : (
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full max-h-64 object-contain"
                        />
                      )}
                      <button
                        onClick={() => {
                          setSelectedFile(null);
                          setPreview(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-black/50 rounded-full hover:bg-black/80"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white flex items-center gap-1">
                        {isVideo ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                        {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Describe what's shown in this media..."
                    rows={2}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 resize-none"
                  />
                </div>

                {/* Upload Button */}
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="w-full py-4 bg-[#C41E3A] text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload {isVideo ? 'Video' : 'Image'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

// Media Gallery Component - displays uploaded media
export function MediaGallery({ media = [], photos = [], onDelete, canDelete = false, autoPlayVideos = false }) {
  const allMedia = [
    ...media.filter(m => !m.is_deleted).map(m => ({ ...m, source: 'media' })),
    ...photos.map(p => ({ ...p, source: 'photo', category: p.type }))
  ];

  if (allMedia.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No media uploaded yet</p>
      </div>
    );
  }

  // Group by category
  const grouped = {
    before: allMedia.filter(m => m.category === 'before'),
    after: allMedia.filter(m => m.category === 'after'),
    damage: allMedia.filter(m => m.category === 'damage')
  };

  return (
    <div className="space-y-6">
      {CATEGORIES.map(cat => {
        const items = grouped[cat.id];
        if (!items?.length) return null;

        return (
          <div key={cat.id}>
            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2`}>
              <span className={`w-2 h-2 rounded-full ${cat.color}`} />
              {cat.label} ({items.length})
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map(item => (
                <MediaItem 
                  key={item.id} 
                  item={item} 
                  onDelete={onDelete}
                  canDelete={canDelete}
                  autoPlay={autoPlayVideos}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MediaItem({ item, onDelete, canDelete, autoPlay }) {
  const [playing, setPlaying] = useState(autoPlay);
  const [deleting, setDeleting] = useState(false);
  const videoRef = useRef(null);

  const isVideo = item.type === 'video';
  const url = item.source === 'media' 
    ? `${API}/api/media/${item.id}` 
    : item.url;

  const handleDelete = async () => {
    if (!confirm('Delete this media?')) return;
    setDeleting(true);
    try {
      await onDelete?.(item);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="relative group rounded-xl overflow-hidden bg-zinc-800 aspect-video">
      {isVideo ? (
        <>
          <video
            ref={videoRef}
            src={url}
            className="w-full h-full object-cover"
            autoPlay={autoPlay}
            muted={autoPlay}
            loop
            playsInline
            onClick={() => {
              if (videoRef.current) {
                if (playing) videoRef.current.pause();
                else videoRef.current.play();
                setPlaying(!playing);
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {!playing && (
              <div className="p-3 bg-black/50 rounded-full">
                <Play className="w-6 h-6 text-white" />
              </div>
            )}
          </div>
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white flex items-center gap-1">
            <Video className="w-3 h-3" />
            Video
          </div>
        </>
      ) : (
        <img
          src={url}
          alt={item.notes || 'Media'}
          className="w-full h-full object-cover"
        />
      )}

      {/* Overlay with delete */}
      {canDelete && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 bg-red-600 rounded-full hover:bg-red-700"
          >
            {deleting ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Trash2 className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      )}

      {/* Notes tooltip */}
      {item.notes && (
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-xs text-white truncate">{item.notes}</p>
        </div>
      )}
    </div>
  );
}

export default MediaUploadButton;
